/** API client — all fetch calls centralized here */

export interface StatEntry {
    status: string;
    count: number;
    total_size: number | null;
    total_duration: number | null;
}

export interface StreamData {
    id: number;
    stream_key: string;
    product_id: number | null;
    event_id: number | null;
    type: string | null;
    title: string | null;
    page_url: string | null;
    raw_meta: string | null;
    resolution: string | null;
    bandwidth: number | null;
    thumb_s3_key: string | null;
    created_at: string;
    ended_at: string | null;
    status: string;
    running: boolean;
}

export interface EventData {
    id: number;
    product_id: number;
    slug: string;
    page_url: string | null;
    title: string | null;
    thumb_url: string | null;
    first_seen_at: string;
    live_seen_at: string | null;
    dvr_seen_at: string | null;
}

export interface ProductData {
    id: number;
    slug: string;
    url: string;
    label: string | null;
    year: number | null;
    description: string | null;
}

export interface StreamStats {
    stats: StatEntry[];
    audioStats: StatEntry[] | null;
}

export interface ChunkMapData {
    chunks: Array<{ sequence: number; status: string }>;
    audioChunks: Array<{ sequence: number; status: string }>;
    min_seq: number;
    max_seq: number;
}

export interface TreeStatRow {
    product_id: number | null;
    title: string | null;
    stream_key: string;
    total_size: number;
    total_duration: number;
    stored: number;
    total: number;
}

export interface WatchData {
    id: number;
    package_url: string;
    label: string | null;
    enabled: number;
    dvr_backload: number;
    last_scan: string | null;
}

export interface ScanResult {
    events: Array<{ id: number; title: string; href: string; url: string; type: string; state: string; thumb?: string }>;
    live: Array<any>;
    dvr: Array<any>;
    tbd: Array<any>;
}

export interface CaptionHit {
    stream_key: string;
    title: string | null;
    t0_ms: number;
    t1_ms: number;
    kind: string;   // speech | ocr
    text: string;
}

export interface LiveBlock {
    id: number;
    stream_key: string;
    t0_ms: number;
    t1_ms: number;
    status: string;   // pending | running | done | error
    text: string | null;
    created_at: string;
}

export interface LiveBlocksResponse {
    stream_key: string;
    gate: { state: string; sinceMs: number; cursor: number; pending: number } | null;
    blocks: LiveBlock[];
}

/** SSE event types from /api/feed */
export type FeedEvent =
    | { type: "chunk"; streamKey: string; sequence: number; size: number; ts: number }
    | { type: "stream:started" | "stream:ended" | "stream:discovered"; streamKey: string; ts: number };

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

// Auth failures surface globally (banner in App) instead of dissolving into
// per-query JSON parse errors that render as silently-empty lists.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
    onUnauthorized = fn;
}

const f = async (url: string, opts?: RequestInit) => {
    const r = await fetch(url, opts);
    if (!r.ok) {
        if (r.status === 401) onUnauthorized?.();
        const body = await r.text().catch(() => "");
        throw new ApiError(r.status, `${r.status} ${url}: ${body.slice(0, 200) || r.statusText}`);
    }
    return r.json();
};
const post = (url: string, body: any) => f(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

export const api = {
    streams: (): Promise<StreamData[]> => f("/api/streams"),
    streamStats: (key: string): Promise<StreamStats> => f(`/api/streams/${encodeURIComponent(key)}/stats`),
    treeStats: (): Promise<TreeStatRow[]> => f("/api/tree-stats"),
    products: (): Promise<ProductData[]> => f("/api/products"),
    events: (): Promise<EventData[]> => f("/api/events"),
    chunks: (key: string): Promise<ChunkMapData> => f(`/api/chunks/${encodeURIComponent(key)}`),
    watches: (): Promise<WatchData[]> => f("/api/watches"),

    scan: (packageUrl: string): Promise<ScanResult> => post("/api/scan", { packageUrl }),
    downloadLive: (packageUrl: string) => post("/api/scan/download-live", { packageUrl }),
    downloadDvr: (packageUrl: string) => post("/api/scan/download-dvr", { packageUrl }),
    download: (streamKey: string, pageUrl?: string, hlsUrl?: string) => post("/api/download", { streamKey, pageUrl, hlsUrl }),
    stop: (streamKey: string) => post("/api/stop", { streamKey }),
    retry: (streamKey: string) => post("/api/retry", { streamKey }),

    addWatch: (packageUrl: string, label?: string) => post("/api/watches", { packageUrl, label }),
    removeWatch: (id: number) => f(`/api/watches/${id}`, { method: "DELETE" }),
    toggleWatch: (id: number, enabled: boolean) => post(`/api/watches/${id}/toggle`, { enabled }),
    toggleDvr: (id: number, enabled: boolean) => post(`/api/watches/${id}/dvr`, { enabled }),

    saveCreds: (user: string, pass: string) => post("/api/credentials", { user, pass }),

    metrics: (): Promise<string> => fetch("/metrics").then(r => r.text()),
    settings: (): Promise<Record<string, string>> => f("/api/settings"),
    saveSettings: (settings: Record<string, string>) => post("/api/settings", settings),

    search: (q: string): Promise<CaptionHit[]> => f(`/api/search?q=${encodeURIComponent(q)}`),
    liveBlocks: (key: string): Promise<LiveBlocksResponse> => f(`/api/live/blocks/${encodeURIComponent(key)}`),
    transcribeStatus: (): Promise<{ counts: Record<string, number> }> => f("/api/transcribe/jobs"),
    enqueueTranscription: (streamKey?: string) =>
        post("/api/transcribe/enqueue", streamKey ? { streamKey } : { all: true }),
};
